import React, { FC, useEffect, useMemo, useState } from 'react';
import { Theme, styled, useMediaQuery, useTheme } from '@material-ui/core';
import { BlockType, BlockWithNotes } from '../../../../../config/api/models/Block';
import { useUser } from '../../../../shared/hooks';
import { VideoPlayer } from './VideoPlayer';
import { VideoGroupProgress, VideoStudentProgress } from './VideoProgress';
import { useTranslation } from 'react-i18next';
import { BlockHeader, RelativeWrapper, WhiteContentWrapper } from './atoms/Atoms';
import { BlockIcon } from '../../BlockIcon';
import { FeedbackModule } from '../../../../feedback';
import { useParams } from 'react-router';
import { UrlParams } from 'src/modules/shared/types';
import { AddContentDialog } from '../AddBlockButton/AddContentDialog';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useGetStudentBookmarkQuery } from 'src/modules/shared/slices/bookmark/api/bookmarkApi';

type Props = {
  block: BlockWithNotes;
  pageId: string;
  feedbackEnabled: boolean;
  ref?: Element | undefined;
  selectedBlock?: string | undefined;
  onSelectBlock: Function;
};

const StyledVideoPlayer = styled(VideoPlayer)({
  margin: '0 auto',
  position: 'absolute',
  top: 0,
  left: 0,
  '& iframe': {
    width: '100%',
    height: '100%',
  },
});

const PlayerWrapper = styled('div')({
  position: 'relative',
  paddingTop: '56.25%',
});

const PrintMessage = styled('div')<Theme>(({ theme }) => ({
  display: 'none',
  '@media print': {
    display: 'block',
    fontSize: '1.3rem',
    fontStyle: 'italic',
    color: theme.palette.grey['600'],
  },
}));

const VideoBlock: FC<Props> = ({ block, pageId, selectedBlock, onSelectBlock }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isTeacher, isStudent } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { bookId } = useParams<UrlParams>();
  const bookmarkMode = useAppSelector((state) => state.bookmark.bookmarkMode);

  // rtk
  const { data: studentBookmarksData } = useGetStudentBookmarkQuery(bookId as string, {
    skip: !isStudent,
  });
  const bookmark = useMemo(() => {
    if (studentBookmarksData === undefined) return undefined;
    return studentBookmarksData;
  }, [studentBookmarksData]);
  const isBookmarked =
    bookmark?.length && bookmark.filter((item) => item.blockId === block.id).length;
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const [showFeedback] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = (isOpen: boolean) => {
    setDialogOpen(isOpen);
    onSelectBlock(isOpen ? block.id : undefined);
  };

  useEffect(() => {
    if (selectedBlock === block.id) setDialogOpen(true);
  }, [block.id, selectedBlock]);

  return (
    <>
      <AddContentDialog
        dialogOpen={selectedBlock === block.id && dialogOpen}
        setDialogOpen={handleDialogOpen}
        pageId={pageId}
        bookId={bookId as string}
        edit={selectedBlock === block.id}
        blockId={selectedBlock}
        content={block.content as string}
        type="video"
        validationType="isVideo"
      />
      <RelativeWrapper id={`video_${block.id}`}>
        <div className="hide-on-print">
          <WhiteContentWrapper
            isMobile={isMobile}
            bookmarkMode={bookmarkMode}
            isBookmarked={isBookmarked}
          >
            <BlockHeader>
              <BlockIcon type={BlockType.VIDEO} />
            </BlockHeader>

            {showFeedback && (
              <FeedbackModule pageId={pageId} blockId={block.id} context="block" mode="embedded" />
            )}
            {!!activeFilter && (
              <>
                {isTeacher && <VideoGroupProgress block={block} />}
                {isStudent && <VideoStudentProgress block={block} />}
              </>
            )}
            <PlayerWrapper>
              <StyledVideoPlayer block={block} />
            </PlayerWrapper>
          </WhiteContentWrapper>
        </div>
        <PrintMessage>{'(' + t('trajectory.sections.shared.video.print') + ')'}</PrintMessage>
      </RelativeWrapper>
    </>
  );
};

export default VideoBlock;
